import { useState } from 'react';
import {
    MessageSquare,
    Briefcase,
    Zap,
    ArrowRight,
    Cpu,
    Microscope,
    ShoppingBag,
    Building,
    DollarSign,
    Users,
    Globe,
    Send
} from 'lucide-react';
const sectorStrategies: any = {
    tech: {
        name: "Technology",
        icon: <Cpu className="w-4 h-4" />,
        characteristics: {
            decisionMakers: "CTO, Head of IP",
            timePreference: "Early morning or late evening",
            communicationStyle: "Direct, technical, data-driven",
            focusPoints: ["Technical innovation protection", "Scalability", "Fast-moving market"]
        },
        approach: {
            initial: {
                channel: "Email + LinkedIn",
                timing: "Tuesday/Thursday mornings",
                style: "Technical with metrics",
                template: `Subject: Protecting {{company}}'s Technical Innovation
  
  Dear {{name}},
  
  I noticed {{company}}'s recent developments in {{technology}}. Given your growth rate of {{growth}}%, have you considered how to protect your technical innovations at scale?
  
  Key areas we could discuss:
  1. AI/ML patent strategy
  2. Software protection approach
  3. Open source compliance
  
  Would you have 30 minutes to discuss your IP scaling strategy?`
            },
            followUp: {
                timing: "+2 days",
                channels: ["Email", "LinkedIn", "Twitter"]
            }
        },
        contentFocus: [
            "Technical specifications",
            "ROI metrics",
            "Competitor analysis",
            "Market position data"
        ]
    },
    biotech: {
        name: "Biotech/Pharma",
        icon: <Microscope className="w-4 h-4" />,
        characteristics: {
            decisionMakers: "CSO, Legal Director",
            timePreference: "Mid-morning",
            communicationStyle: "Formal, research-based, compliance-focused",
            focusPoints: ["Research protection", "Regulatory compliance", "Long-term strategy"]
        },
        approach: {
            initial: {
                channel: "Formal email",
                timing: "Wednesday mornings",
                style: "Research-focused",
                template: `Subject: Research Protection Strategy for {{company}}
  
  Dear Dr. {{name}},
  
  I've followed {{company}}'s groundbreaking research in {{field}}. With {{trialPhase}} trials progressing, how are you approaching IP protection for your research pipeline?
  
  Key considerations:
  1. Patent portfolio strategy
  2. Research documentation
  3. Global protection approach
  
  Would you be interested in discussing your IP strategy?`
            },
            followUp: {
                timing: "+4 days",
                channels: ["Email", "Professional networks"]
            }
        },
        contentFocus: [
            "Research validation",
            "Compliance requirements",
            "Patent strategy",
            "Timeline planning"
        ]
    },
    retail: {
        name: "Retail/Consumer",
        icon: <ShoppingBag className="w-4 h-4" />,
        characteristics: {
            decisionMakers: "Brand Director, Legal Counsel",
            timePreference: "Business hours",
            communicationStyle: "Brand-focused, visual, market-oriented",
            focusPoints: ["Brand protection", "Market expansion", "Consumer trust"]
        },
        approach: {
            initial: {
                channel: "Email with visual presentation",
                timing: "Tuesday/Wednesday",
                style: "Brand-centric",
                template: `Subject: Protecting {{company}}'s Brand Value
  
  Dear {{name}},
  
  With {{company}}'s expanding market presence and {{growth}}% YoY growth, how are you protecting your brand across {{markets}} markets?
  
  We could discuss:
  1. Brand protection strategy
  2. Market expansion safeguards
  3. Consumer trust building
  
  When would be convenient to explore your brand protection needs?`
            },
            followUp: {
                timing: "+3 days",
                channels: ["Email", "LinkedIn", "Industry events"]
            }
        },
        contentFocus: [
            "Brand metrics",
            "Market trends",
            "Consumer insights",
            "Competition analysis"
        ]
    },
    fintech: {
        name: "FinTech",
        icon: <DollarSign className="w-4 h-4" />,
        characteristics: {
            decisionMakers: "CTO, Compliance Officer",
            timePreference: "Early morning",
            communicationStyle: "Security-focused, compliance-aware, professional",
            focusPoints: ["Security", "Regulatory compliance", "Scalable protection"]
        },
        approach: {
            initial: {
                channel: "Secure email",
                timing: "Tuesday mornings",
                style: "Compliance-focused",
                template: `Subject: Secure IP Strategy for {{company}}'s FinTech Innovation
  
  Dear {{name}},
  
  Given {{company}}'s position in {{segment}} and recent {{regulation}} requirements, how are you addressing IP protection alongside compliance?
  
  Critical areas:
  1. Technology patent strategy
  2. Regulatory compliance
  3. Security protocols
  
  Would you have time to discuss your IP security approach?`
            },
            followUp: {
                timing: "+2 days",
                channels: ["Email", "Secure meeting platforms"]
            }
        },
        contentFocus: [
            "Security metrics",
            "Compliance records",
            "Technology protection",
            "Risk assessment"
        ]
    }
};

const Main = () => {
    const [selectedSector, setSelectedSector] = useState('tech');
    return (
        <div className="space-y-6">
            {/* Sector Selection */}
            <div className='bg-white p-[25px] rounded-[5px] shadow-2xl'>
                <div>
                    <div className="flex items-center gap-2">
                        <MessageSquare className="w-5 h-5" />
                        <span className='font-bold'> Sector-Specific Communication</span>
                    </div>
                    <div>
                        Tailored communication strategies by industry
                    </div>
                </div>
                <div>
                    <select value={selectedSector} onChange={(e) => setSelectedSector(e.target.value)} className="w-full mt-2 focus:outline-none rounded-2xl py-2 px-4 border-4 border-main text-primary">
                        {Object.entries(sectorStrategies).map(([key, sector]: any) => (
                            <option key={key} value={key}>
                                {sector.name}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Strategy Details */}
            <div className='bg-white p-[25px] rounded-[5px] shadow-2xl'>
                <div>
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            {sectorStrategies[selectedSector].icon}
                            <span className='font-bold'>{sectorStrategies[selectedSector].name} Strategy</span>
                        </div>
                        <div className='text-xs font-medium me-2 px-2.5 py-0.5 rounded-sm bg-blue-100 text-blue-800'>
                            Recommended Approach
                        </div>
                    </div>
                </div>
                <div>
                    <div className="space-y-6 mt-2">
                        {/* Characteristics */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="p-4 border rounded-lg">
                                <h3 className="font-medium mb-3">Audience Profile</h3>
                                <div className="space-y-2">
                                    <div className="flex items-center gap-2 text-sm">
                                        <Users className="w-4 h-4" />
                                        Key stakeholders: {sectorStrategies[selectedSector].characteristics.decisionMakers}
                                    </div>
                                    <div className="flex items-center gap-2 text-sm">
                                        <Globe className="w-4 h-4" />
                                        Style: {sectorStrategies[selectedSector].characteristics.communicationStyle}
                                    </div>
                                </div>
                            </div>

                            <div className="p-4 border rounded-lg">
                                <h3 className="font-medium mb-3">Focus Points</h3>
                                <div className="space-y-2">
                                    {sectorStrategies[selectedSector].characteristics.focusPoints.map((point: any, index: any) => (
                                        <div key={index} className="flex items-center gap-2 text-sm">
                                            <ArrowRight className="w-4 h-4" />
                                            {point}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Communication Approach */}
                        <div className="p-4 border rounded-lg">
                            <h3 className="font-medium mb-3">Communication Approach</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <h4 className="text-sm font-medium mb-2">Initial Contact</h4>
                                    <div className="space-y-2">
                                        <div className="text-sm">Channel: {sectorStrategies[selectedSector].approach.initial.channel}</div>
                                        <div className="text-sm">Timing: {sectorStrategies[selectedSector].approach.initial.timing}</div>
                                        <div className="text-sm">Style: {sectorStrategies[selectedSector].approach.initial.style}</div>
                                    </div>
                                </div>
                                <div>
                                    <h4 className="text-sm font-medium mb-2">Content Focus</h4>
                                    <div className="space-y-2">
                                        {sectorStrategies[selectedSector].contentFocus.map((focus: any, index: any) => (
                                            <div key={index} className="text-sm flex items-center gap-2">
                                                <div className="w-1 h-1 bg-blue-500 rounded-full"></div>
                                                {focus}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Template Preview */}
                        <div className="p-4 border rounded-lg">
                            <h3 className="font-medium mb-3">Message Template</h3>
                            <div className="bg-gray-50 p-3 rounded text-sm font-mono whitespace-pre-wrap">
                                {sectorStrategies[selectedSector].approach.initial.template}
                            </div>
                            <div className="flex justify-end mt-4">
                                <button className="flex items-center gap-2 px-4 py-2 bg-green text-white rounded-lg">
                                    <Send className="w-4 h-4" />
                                    Use Template
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
export default Main